import React from "react";
import { LayoutElementType } from "@/components/layout/element-layout/layoutTypes";
import CardBase from "@/components/cards/CardBase";
import StatsWidget from "@/components/cards/widgets/StatsWidget";
import ListWidget from "@/components/cards/widgets/ListWidget";
import TableWidget, { TableRow } from "@/components/cards/widgets/TableWidget";
import TMSTableWidget from "@/components/cards/widgets/TMSWidgets/TMSTableWidget";
import TitleWidget from "@/components/cards/widgets/TitleWidget";
import TextFieldRegular from "@/components/cards/text-fields/TextFieldRegular";
import GridWidget from "@/components/cards/widgets/GridWidget";
import FormWidget from "@/components/cards/widgets/FormWidget";
import TypesWidget from "@/components/cards/widgets/Types/TypesWidget";
import TagsWidget from "@/components/cards/widgets/TagsWidget/TagsWidget";
import { DataSourceType, ListWidgetItem, TableColumn, cardDataTransformers } from "@/lib/dataSources";
import { useDataSource } from "@/lib/useDataSource"; 
import { useMemo } from "react";
import { useFolderContext } from "@/providers/FolderProvider";


import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
// Wrapper components to handle type conversion and ensure widgets are self-contained
const ListWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const { showTitle = true, ...listProps } = props;
  return (
    <div className="h-full w-full">
      <ListWidget {...listProps} showTitle={showTitle as boolean} />
    </div>
  );
};

const StatsWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const { showTitle = true, ...statsProps } = props;
  return (
    <div className="h-full w-full">
      <StatsWidget {...statsProps} showTitle={showTitle as boolean} />
    </div>
  );
};

const TableWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    columns,
    data,
    dataSource,
    backgroundColor,
    showTitle = true,
    ...rest
  } = props;
  const validDataSource =
    typeof dataSource === "string" && dataSource ? dataSource : undefined;

  return (
    <div
      className="h-full w-full"
      style={{ backgroundColor: backgroundColor as string }}
    >
      <TableWidget
        columns={columns as TableColumn[]}
        data={data as TableRow[] }
        dataSource={validDataSource as DataSourceType}
        showTitle={showTitle as boolean || true}
        {...rest}
      />
    </div>
  );
};

const TMSTableWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    showTitle = true,
    title = "TMS Table",
    backgroundColor = "transparent",
    shadowType = "black",
    shadowColor = "#000000",
    ...rest
  } = props;

  return (
    <div
      className="h-full w-full"
      style={{ backgroundColor: backgroundColor as string }}
    >
      <TMSTableWidget
        showTitle={showTitle as boolean}
        title={title as string}
        backgroundColor={backgroundColor as string}
        shadowType={shadowType as "black" | "gradient" | "none" | "color"}
        shadowColor={shadowColor as string}
        {...rest}
      />
    </div>
  );
};



const TitleWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const { backgroundColor, ...titleProps } = props;
  return (
    <div
      className="h-full w-full"
      style={{ backgroundColor: backgroundColor as string }}
    >
      <TitleWidget {...titleProps} />
    </div>
  );
};

const TextFieldWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    backgroundColor,
    initialValue = "",
    placeholder = "Start typing...",
    readOnly = false,
    onValueChange,
    ...textFieldProps
  } = props;

  return (
    <div
      className="h-full w-full"
      style={{ backgroundColor: backgroundColor as string }}
    >
      <TextFieldRegular
        initialValue={initialValue as string}
        placeholder={placeholder as string}
        readOnly={readOnly as boolean}
        onValueChange={onValueChange as (value: string) => void}
        {...textFieldProps}
      />
    </div>
  );
};

// Sub-component for data-source enabled GridWidget
const DataSourceGridWidget: React.FC<Record<string, unknown> & { dataSource: DataSourceType }> = (props) => {
  const { dataSource, ...otherProps } = props;
  const { selectedFolderId } = useFolderContext();
  const { data } = useDataSource(dataSource, selectedFolderId);
  console.log(data)
  
  // Create appropriate onAdd handler based on data source
  const handleAdd = useMemo(() => {
    if (dataSource === "documents") {
      return () => {
        // Trigger document creation event
        window.dispatchEvent(new CustomEvent('addDocument'));
      };
    }
    if (dataSource === "objects") {
      return () => {
        // Trigger glossary item creation event
        window.dispatchEvent(new CustomEvent('addObject'));
      };
    }
    if (dataSource === "objects-sync") {
      return () => {
        // Trigger blueprint creation event
        window.dispatchEvent(new CustomEvent('addObjectSync'));
      };
    }
    return undefined;
  }, [dataSource]);
  
  const cardsData = useMemo(() => {
    if (dataSource === "documents") {
      const transformed = cardDataTransformers.documents(data || []);
      return transformed;
    }
    if (dataSource === "objects") {
      const transformed = cardDataTransformers.objects(data || []);
      return transformed;
    }
    if (dataSource === "objects-sync") {
      const transformed = cardDataTransformers['objects-sync' as keyof typeof cardDataTransformers & keyof typeof cardDataTransformers  ](data || []);
      return transformed;
    }
    return [];
  }, [dataSource, data]);

  return <GridWidget {...otherProps} cards={cardsData} onAdd={handleAdd} />;
}; 
const GridWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    title ,
    cards = [],
    groupedCards,
    dataSource,
    onCardClick,
    onEdit,
    onDelete,
    onAdd,
    onBulkAdd,
    showHeader = true,
    showSearch = true,
    showTagFilters = true,
    showGridSizeSelector = true,
    backgroundColor,
    filterConfig,
    filterActions,
    itemType,
    // Add the new data view mode props
    availableSections,
    availableFields,
    rawItems,
    templateLookup,
    // Cell value change callback for table editing
    onCellValueChange,
    // Pending changes props
    pendingChangesCount,
    hasPendingChanges,
    modifiedItems,
    modifiedCells,
    onSaveItem,
    onDiscardItem,
    onSaveAllItems,
    onDiscardAllItems,
    onForceSaveItem,
    // Folder path lookup for displaying folder paths instead of IDs
    folderPathLookup,
    // Bulk delete handler
    onDeleteSelected,
  } = props;
  const { t } = usePlatformTranslation();     
  // If dataSource is provided, use the data-source component
  if (typeof dataSource === "string") {
    return (
      <DataSourceGridWidget
        title={title as string}
        dataSource={dataSource as DataSourceType}
        onCardClick={onCardClick as any}
        onEdit={onEdit as any}
        onDelete={onDelete as any}
        onAdd={onAdd as any}
        onBulkAdd={onBulkAdd as any}
        showHeader={showHeader as boolean}
        showSearch={showSearch as boolean}
        showTagFilters={showTagFilters as boolean}
        showGridSizeSelector={showGridSizeSelector as boolean}
        backgroundColor={backgroundColor as string}
      />
    );
  }

  // Otherwise, use the cards prop directly
  return (
    <GridWidget
      title={title as string}
      cards={cards as any[]}
      groupedCards={groupedCards as any}
      onCardClick={onCardClick as any}
      onEdit={onEdit as any}
      onDelete={onDelete as any}
      onAdd={onAdd as any}
      onBulkAdd={onBulkAdd as any}
      showHeader={showHeader as boolean}
      showSearch={showSearch as boolean}
      showTagFilters={showTagFilters as boolean}
      showGridSizeSelector={showGridSizeSelector as boolean}
      backgroundColor={backgroundColor as string}
      filterConfig={filterConfig as any}
      filterActions={filterActions as any}
      itemType={itemType as string}
      // Pass the new data view mode props
      availableSections={availableSections as any}
      availableFields={availableFields as any}
      rawItems={rawItems as any}
      templateLookup={templateLookup as any}
      onCellValueChange={onCellValueChange as any}
      // Pending changes props
      pendingChangesCount={pendingChangesCount as number}
      hasPendingChanges={hasPendingChanges as boolean}
      modifiedItems={modifiedItems as Set<string>}
      modifiedCells={modifiedCells as Map<string, string[]>}
      onSaveItem={onSaveItem as any}
      onDiscardItem={onDiscardItem as any}
      onSaveAllItems={onSaveAllItems as any}
      onDiscardAllItems={onDiscardAllItems as any}
      onForceSaveItem={onForceSaveItem as any}
      folderPathLookup={folderPathLookup as any}
      // Bulk delete handler
      onDeleteSelected={onDeleteSelected as any}
    />
  );
};

const FormWidgetWrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    title = "Form",
    sections = [],
    onDataChange,
    showTitle = true,
    backgroundColor,
  } = props;
  const { t } = usePlatformTranslation();
  return (
    <FormWidget
      title={title as string}
      sections={sections as any[]}
      onDataChange={
        onDataChange as (data: Record<string, string | number>) => void
      }
      showTitle={showTitle as boolean}
      backgroundColor={backgroundColor as string}
    />
  );
};

// Row Layout Wrapper Components
const RowLayout1Wrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    backgroundColor,
    title = "Single Element Row",
    elements = [],
    ...rowProps
  } = props;

  // Create a layout config for 1 slot
  const layoutConfig = {
    elements: elements as unknown[],
    totalSlots: 1,
    gap: 40,
    padding: 0,
  };
  const { t } = usePlatformTranslation();

  return (
    <CardBase className="h-full">
      <div
        className="w-full h-full"
        style={{ backgroundColor: backgroundColor as string }}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700">
            {title as string}
          </div>
        </div>
        <div className="flex-1 p-4">
          <div className="h-full">
            {/* This would need to be replaced with actual ElementLayout integration */}
            <div className="h-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.single_element_slot')}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Add widgets here
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardBase>
  );
};

const RowLayout2Wrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    backgroundColor,
    title = "Two Element Row",
    elements = [],
    ...rowProps
  } = props;
  const { t } = usePlatformTranslation(); 
  return (
    <CardBase className="h-full">
      <div
        className="w-full h-full"
        style={{ backgroundColor: backgroundColor as string }}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700">
            {title as string}
          </div>
        </div>
        <div className="flex-1 p-4">
          <div className="h-full flex gap-4">
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">Slot 1</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">Slot 2</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardBase>
  );
};

const RowLayout3Wrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    backgroundColor,
    title = "Three Element Row",
    elements = [],
    ...rowProps
  } = props;
  const { t } = usePlatformTranslation();
  return (
    <CardBase className="h-full">
      <div
        className="w-full h-full"
        style={{ backgroundColor: backgroundColor as string }}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700">
            {title as string}
          </div>
        </div>
        <div className="flex-1 p-4">
          <div className="h-full flex gap-4">
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_1')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_2')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_3')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardBase>
  );
};

const RowLayout4Wrapper: React.FC<Record<string, unknown>> = (props) => {
  const {
    backgroundColor,
    title = "Four Element Row",
    elements = [],
    ...rowProps
  } = props;
  const { t } = usePlatformTranslation();


  return (
    <CardBase className="h-full">
      <div
        className="w-full h-full"
        style={{ backgroundColor: backgroundColor as string }}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700">
            {title as string}
          </div>
        </div>
        <div className="flex-1 p-4">
          <div className="h-full flex gap-4">
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_1_2_3')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_2_2_3')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_3_2')}</div>
                <div className="text-xs text-gray-500 mt-1">Add widget</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('platform.layout.element-layout.slot_4')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('platform.layout.element-layout.add_widget_2_3_4_5_6_7_8_9')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardBase>
  );
};



// Component registry - only widget elements, no layout-specific elements
export const layoutElements: Record<
  LayoutElementType,
  React.ComponentType<Record<string, unknown>>
> = {
  "stats-widget": StatsWidgetWrapper,
  "row-layout-1": RowLayout1Wrapper,
  "row-layout-2": RowLayout2Wrapper,
  "row-layout-3": RowLayout3Wrapper,
  "row-layout-4": RowLayout4Wrapper,
  "list-widget": ListWidgetWrapper,
  "table-widget": TableWidgetWrapper,
  "tms-table-widget": TMSTableWidgetWrapper,
  "title-widget": TitleWidgetWrapper,
  "text-field-widget": TextFieldWidgetWrapper,
  "grid-widget": GridWidgetWrapper,
  "form-widget": FormWidgetWrapper,
  "types-widget": TypesWidget,
  "tags-widget": TagsWidget,
  "expandable-table-widget": () => null,
  "chart-widget": () => null,
  "calendar-widget": () => null,
  "notification-widget": () => null,
  "progress-widget": () => null,
  "media-widget": () => null,
};

// Export the component registry for easy access
export default layoutElements;
